// Customer.jsx — Full Customer Management Page
// Redesigned to match app's dark glassmorphism theme
// Features: Add / Edit / Delete customers + existing bill-view modal preserved
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://omapi.omgoldfinance.live/api';

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ======================= STYLES =======================
const S = {
  page: {
    minHeight: '100vh',
    background: '#0f172a',
    padding: '28px 24px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: '#f1f5f9',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '28px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerLeft: {},
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#f8fafc',
    margin: 0,
    background: 'linear-gradient(135deg, #a78bfa, #818cf8)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    color: '#94a3b8',
    fontSize: '14px',
    marginTop: '4px',
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 22px',
    background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)',
  },

  // Stats row
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    padding: '16px 20px',
    backdropFilter: 'blur(10px)',
  },
  statLabel: { fontSize: '12px', color: '#94a3b8', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statValue: { fontSize: '28px', fontWeight: '700', color: '#f1f5f9' },
  statAccent: { color: '#a78bfa' },

  // Search / filter bar
  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchWrap: {
    flex: 1,
    minWidth: '220px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '16px',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '10px 14px 10px 38px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },

  // Table card
  tableCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'rgba(0,0,0,0.3)' },
  th: {
    padding: '14px 18px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  thCenter: { textAlign: 'center' },
  td: {
    padding: '14px 18px',
    fontSize: '14px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    color: '#e2e8f0',
    verticalAlign: 'middle',
  },
  tdCenter: { textAlign: 'center' },
  nameCell: { fontWeight: '600', color: '#f1f5f9' },
  phoneChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    background: 'rgba(99, 102, 241, 0.15)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: '13px',
    color: '#818cf8',
    fontFamily: 'monospace',
  },
  idProofBadge: {
    display: 'inline-block',
    background: 'rgba(16, 185, 129, 0.15)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    color: '#6ee7b7',
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '500',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  idProofMissing: {
    display: 'inline-block',
    background: 'rgba(245, 158, 11, 0.15)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    color: '#fbbf24',
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: '12px',
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  editBtn: {
    padding: '6px 14px',
    background: 'rgba(59, 130, 246, 0.2)',
    border: '1px solid rgba(59, 130, 246, 0.4)',
    borderRadius: '7px',
    color: '#60a5fa',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    padding: '6px 14px',
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '7px',
    color: '#f87171',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  viewBillsBtn: {
    padding: '6px 14px',
    background: 'rgba(168, 85, 247, 0.15)',
    border: '1px solid rgba(168, 85, 247, 0.35)',
    borderRadius: '7px',
    color: '#c084fc',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },

  // Pagination
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pgInfo: { fontSize: '13px', color: '#94a3b8' },
  pgBtns: { display: 'flex', gap: '6px' },
  pgBtn: {
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '7px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  pgBtnActive: {
    background: 'rgba(124, 58, 237, 0.5)',
    border: '1px solid rgba(124, 58, 237, 0.7)',
    color: '#fff',
    fontWeight: '600',
  },
  pgBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },

  // Empty state
  empty: {
    textAlign: 'center',
    padding: '60px 24px',
    color: '#64748b',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyText: { fontSize: '16px' },

  // Loading spinner
  spinnerWrap: { textAlign: 'center', padding: '60px 24px' },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(167, 139, 250, 0.2)',
    borderTopColor: '#a78bfa',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px',
  },

  // Alert
  alert: {
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '14px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  alertError: {
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5',
  },
  alertSuccess: {
    background: 'rgba(16,185,129,0.15)',
    border: '1px solid rgba(16,185,129,0.3)',
    color: '#6ee7b7',
  },

  // ============ MODAL ============
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(6px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    animation: 'fadeIn 0.2s ease',
  },
  modal: {
    background: '#1e293b',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '18px',
    maxWidth: '560px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
    animation: 'slideUp 0.25s ease',
  },
  modalLarge: {
    maxWidth: '1000px',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(79,70,229,0.2))',
    borderRadius: '18px 18px 0 0',
  },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#f1f5f9', margin: 0 },
  modalClose: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: '#94a3b8',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  modalBody: { padding: '24px' },
  modalFooter: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },

  // Form elements in modal
  formGroup: { marginBottom: '18px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#94a3b8',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  requiredStar: { color: '#f87171', marginLeft: '2px' },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  cancelBtn: {
    padding: '10px 22px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  saveBtn: {
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
  },
  saveBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  confirmDeleteBtn: {
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // Bill modal sub-table
  billTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  billTh: {
    padding: '10px 12px',
    background: '#0f172a',
    color: '#94a3b8',
    textAlign: 'left',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: '600',
  },
  billTd: {
    padding: '10px 12px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    color: '#cbd5e1',
    fontSize: '13px',
  },
  payBadgePaid: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '20px',
    background: 'rgba(16,185,129,0.2)',
    color: '#6ee7b7',
    fontSize: '11px',
    fontWeight: '600',
  },
  payBadgePartial: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '20px',
    background: 'rgba(245,158,11,0.2)',
    color: '#fbbf24',
    fontSize: '11px',
    fontWeight: '600',
  },
  payBadgePending: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '20px',
    background: 'rgba(239,68,68,0.2)',
    color: '#f87171',
    fontSize: '11px',
    fontWeight: '600',
  },

  // SNO badge
  snoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    background: 'rgba(124, 58, 237, 0.2)',
    borderRadius: '7px',
    fontWeight: '700',
    fontSize: '13px',
    color: '#a78bfa',
  },
};

// ======================= COMPONENT =======================
const CustomerDetailsPage = () => {
  // ---- Customer CRUD state ----
  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // ---- Add/Edit modal state ----
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null); // null = add mode
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', idProof: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // ---- Delete modal state ----
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ---- Bill view modal state (existing functionality preserved) ----
  const [showBillModal, setShowBillModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBills, setCustomerBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);

  // ---- Initial load ----
  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm]);

  // ---- Debounce search ----
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ===================== API CALLS =====================
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        search: searchTerm,
      });
      const res = await api.get(`/customers?${params}`);
      const data = res.data;

      if (data.success) {
        setCustomers(data.customers);
        setTotalPages(data.pages || 1);
        setTotalCustomers(data.total || 0);
      } else {
        throw new Error(data.error || 'Failed to fetch customers');
      }
    } catch (err) {
      setError(err.message);
      console.error('Fetch customers error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const isEdit = !!editingCustomer;
      const url = isEdit
        ? `${API_BASE_URL}/customers/${editingCustomer.id}`
        : `${API_BASE_URL}/customers`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await api.request({
        url,
        method,
        data: formData,
      });
      const data = res.data;
      if (res.status < 200 || res.status >= 300) throw new Error(data.error || 'Failed to save');

      setSuccess(isEdit ? 'Customer updated successfully!' : 'Customer added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      closeFormModal();
      fetchCustomers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/customers/${customerToDelete.id}`);
      const data = res.data;
      if (res.status < 200 || res.status >= 300) throw new Error(data.error || 'Failed to delete');

      setSuccess(`Customer "${customerToDelete.name}" deleted.`);
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteModal(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ===================== BILL MODAL (preserved) =====================
  const handleViewCustomerBills = async (customer) => {
    setSelectedCustomer(customer);
    setShowBillModal(true);
    setLoadingBills(true);
    try {
      let allBills = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.get(`/billing/bills?page=${page}&per_page=100`);
        const data = res.data;
        const matched = data.bills.filter(bill => {
          const billPhone = bill.customer?.phone || bill.customerPhone;
          const billName = bill.customer?.name || bill.customerName;
          return (billPhone && billPhone === customer.phone) ||
                 (billName && billName === customer.name);
        });
        allBills = [...allBills, ...matched];
        hasMore = page < data.pages;
        page++;
      }
      setCustomerBills(allBills);
    } catch (err) {
      console.error('Error fetching bills:', err);
    } finally {
      setLoadingBills(false);
    }
  };

  const closeBillModal = () => {
    setShowBillModal(false);
    setSelectedCustomer(null);
    setCustomerBills([]);
  };

  // ===================== MODAL HELPERS =====================
  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData({ name: '', phone: '', address: '', idProof: '' });
    setFormError('');
    setShowFormModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || '',
      idProof: customer.idProof || '',
    });
    setFormError('');
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingCustomer(null);
    setFormError('');
  };

  const openDeleteModal = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  // ===================== UTILS =====================
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return '—'; }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return `₹${Number(amount).toFixed(2)}`;
  };

  const getPayBadge = (status) => {
    const s = (status || 'pending').toLowerCase();
    if (s === 'paid') return <span style={S.payBadgePaid}>Paid</span>;
    if (s === 'partial') return <span style={S.payBadgePartial}>Partial</span>;
    return <span style={S.payBadgePending}>Pending</span>;
  };

  const customersWithIdProof = customers.filter(c => c.idProof && c.idProof !== 'Pending').length;

  const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;

  // ===================== RENDER =====================
  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .cust-row:hover { background: rgba(255,255,255,0.04) !important; }
        .cust-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        .cust-input:focus { border-color: rgba(124,58,237,0.6) !important; box-shadow: 0 0 0 3px rgba(124,58,237,0.15); }
        .add-btn-main:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(124,58,237,0.5) !important; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); }
        ::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.4); border-radius: 6px; }
      `}</style>

      {/* ===== PAGE HEADER ===== */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <h1 style={S.title}>Customer Management</h1>
          <p style={S.subtitle}>Manage all customer records — add, edit, and view bill history</p>
        </div>
        <button
          style={S.addBtn}
          className="cust-btn add-btn-main"
          onClick={openAddModal}
        >
          <span style={{ fontSize: '18px' }}>＋</span> Add Customer
        </button>
      </div>

      {/* ===== STATS CARDS ===== */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statLabel}>Total Customers</div>
          <div style={{ ...S.statValue, ...S.statAccent }}>{totalCustomers}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>With ID Proof</div>
          <div style={{ ...S.statValue, color: '#6ee7b7' }}>{customersWithIdProof}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>Pending ID Proof</div>
          <div style={{ ...S.statValue, color: '#fbbf24' }}>{totalCustomers - customersWithIdProof}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>Current Page</div>
          <div style={{ ...S.statValue, color: '#60a5fa' }}>{currentPage} / {totalPages}</div>
        </div>
      </div>

      {/* ===== ALERTS ===== */}
      {error && (
        <div style={{ ...S.alert, ...S.alertError }}>
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div style={{ ...S.alert, ...S.alertSuccess }}>
          ✅ {success}
        </div>
      )}

      {/* ===== SEARCH BAR ===== */}
      <div style={S.filterBar}>
        <div style={S.searchWrap}>
          <span style={S.searchIcon}>🔍</span>
          <input
            type="text"
            style={{ ...S.searchInput }}
            className="cust-input"
            placeholder="Search by name, phone, address or ID proof…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          style={{
            padding: '10px 18px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '10px',
            color: '#94a3b8',
            fontSize: '13px',
            cursor: 'pointer',
          }}
          className="cust-btn"
          onClick={() => { setSearchTerm(''); setCurrentPage(1); fetchCustomers(); }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* ===== CUSTOMERS TABLE ===== */}
      <div style={S.tableCard}>
        {loading ? (
          <div style={S.spinnerWrap}>
            <div style={S.spinner} />
            <p style={{ color: '#64748b' }}>Loading customers…</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead style={S.thead}>
                  <tr>
                    <th style={S.th}>#</th>
                    <th style={S.th}>Customer Name</th>
                    <th style={S.th}>Phone</th>
                    <th style={S.th}>Address</th>
                    <th style={S.th}>ID Proof</th>
                    <th style={S.th}>Added On</th>
                    <th style={{ ...S.th, ...S.thCenter }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ padding: 0 }}>
                        <div style={S.empty}>
                          <div style={S.emptyIcon}>👥</div>
                          <div style={S.emptyText}>
                            {searchTerm ? 'No customers match your search.' : 'No customers yet. Click "Add Customer" to get started.'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer, idx) => (
                      <tr key={customer.id} className="cust-row" style={{ transition: 'background 0.15s' }}>
                        <td style={S.td}>
                          <span style={S.snoBadge}>{indexOfFirstItem + idx + 1}</span>
                        </td>
                        <td style={{ ...S.td, ...S.nameCell }}>
                          {customer.name}
                        </td>
                        <td style={S.td}>
                          <span style={S.phoneChip}>📞 {customer.phone}</span>
                        </td>
                        <td style={{ ...S.td, maxWidth: '200px' }}>
                          <span style={{ color: customer.address ? '#cbd5e1' : '#475569', fontSize: '13px' }}>
                            {customer.address
                              ? (customer.address.length > 40 ? customer.address.substring(0, 40) + '…' : customer.address)
                              : '—'}
                          </span>
                        </td>
                        <td style={S.td}>
                          {customer.idProof && customer.idProof !== 'Pending' ? (
                            <span style={S.idProofBadge} title={customer.idProof}>
                              ✓ {customer.idProof.length > 18 ? customer.idProof.substring(0, 18) + '…' : customer.idProof}
                            </span>
                          ) : (
                            <span style={S.idProofMissing}>⚠ Pending</span>
                          )}
                        </td>
                        <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b' }}>
                          {formatDate(customer.createdAt)}
                        </td>
                        <td style={{ ...S.td, ...S.tdCenter }}>
                          <div style={S.actionsCell}>
                            <button
                              style={S.editBtn}
                              className="cust-btn"
                              onClick={() => openEditModal(customer)}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              style={S.viewBillsBtn}
                              className="cust-btn"
                              onClick={() => handleViewCustomerBills(customer)}
                            >
                              📋 Bills
                            </button>
                            <button
                              style={S.deleteBtn}
                              className="cust-btn"
                              onClick={() => openDeleteModal(customer)}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={S.pagination}>
                <span style={S.pgInfo}>
                  Showing {indexOfFirstItem + 1}–{Math.min(indexOfFirstItem + ITEMS_PER_PAGE, totalCustomers)} of {totalCustomers} customers
                </span>
                <div style={S.pgBtns}>
                  <button
                    style={{ ...S.pgBtn, ...(currentPage === 1 ? S.pgBtnDisabled : {}) }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >← Prev</button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pg = i + 1;
                    if (totalPages > 5) {
                      if (currentPage <= 3) pg = i + 1;
                      else if (currentPage >= totalPages - 2) pg = totalPages - 4 + i;
                      else pg = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={i}
                        style={{ ...S.pgBtn, ...(currentPage === pg ? S.pgBtnActive : {}) }}
                        onClick={() => setCurrentPage(pg)}
                      >
                        {pg}
                      </button>
                    );
                  })}
                  <button
                    style={{ ...S.pgBtn, ...(currentPage === totalPages ? S.pgBtnDisabled : {}) }}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ===== ADD / EDIT CUSTOMER MODAL ===== */}
      {showFormModal && (
        <div style={S.overlay} onClick={closeFormModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={S.modalTitle}>
                {editingCustomer ? '✏️ Edit Customer' : '➕ Add New Customer'}
              </h2>
              <button style={S.modalClose} className="cust-btn" onClick={closeFormModal}>✕</button>
            </div>

            <form onSubmit={handleSaveCustomer}>
              <div style={S.modalBody}>
                {formError && (
                  <div style={{ ...S.alert, ...S.alertError, marginBottom: '16px' }}>
                    ⚠️ {formError}
                  </div>
                )}

                <div style={S.formGroup}>
                  <label style={S.label}>
                    Customer Name <span style={S.requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    style={S.input}
                    className="cust-input"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    required
                    autoFocus
                  />
                </div>

                <div style={S.formGroup}>
                  <label style={S.label}>
                    Phone Number <span style={S.requiredStar}>*</span>
                  </label>
                  <input
                    type="tel"
                    style={S.input}
                    className="cust-input"
                    placeholder="10-digit phone number"
                    value={formData.phone}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData(p => ({ ...p, phone: v }));
                    }}
                    required
                  />
                </div>

                <div style={S.formGroup}>
                  <label style={S.label}>Address</label>
                  <textarea
                    style={S.textarea}
                    className="cust-input"
                    placeholder="Full address (optional)"
                    value={formData.address}
                    onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div style={S.formGroup}>
                  <label style={S.label}>
                    ID Proof <span style={S.requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    style={S.input}
                    className="cust-input"
                    placeholder="Aadhaar / PAN / Voter ID / Passport number"
                    value={formData.idProof}
                    onChange={e => setFormData(p => ({ ...p, idProof: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div style={S.modalFooter}>
                <button type="button" style={S.cancelBtn} className="cust-btn" onClick={closeFormModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ ...S.saveBtn, ...(formLoading ? S.saveBtnDisabled : {}) }}
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving…' : (editingCustomer ? 'Update Customer' : 'Add Customer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== DELETE CONFIRM MODAL ===== */}
      {showDeleteModal && customerToDelete && (
        <div style={S.overlay} onClick={() => { setShowDeleteModal(false); setCustomerToDelete(null); }}>
          <div style={{ ...S.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ ...S.modalTitle, color: '#f87171' }}>🗑 Delete Customer</h2>
              <button
                style={S.modalClose}
                onClick={() => { setShowDeleteModal(false); setCustomerToDelete(null); }}
              >✕</button>
            </div>
            <div style={S.modalBody}>
              <p style={{ color: '#cbd5e1', lineHeight: '1.6', fontSize: '15px' }}>
                Are you sure you want to delete{' '}
                <strong style={{ color: '#f1f5f9' }}>{customerToDelete.name}</strong>
                {' '}({customerToDelete.phone})?
              </p>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>
                This action cannot be undone. Bill history will not be affected.
              </p>
            </div>
            <div style={S.modalFooter}>
              <button
                style={S.cancelBtn}
                className="cust-btn"
                onClick={() => { setShowDeleteModal(false); setCustomerToDelete(null); }}
              >Cancel</button>
              <button
                style={S.confirmDeleteBtn}
                className="cust-btn"
                onClick={handleDeleteCustomer}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== BILL VIEW MODAL (existing functionality fully preserved) ===== */}
      {showBillModal && selectedCustomer && (
        <div style={S.overlay} onClick={closeBillModal}>
          <div style={{ ...S.modal, ...S.modalLarge }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <h2 style={S.modalTitle}>📋 Bill History</h2>
                <p style={{ color: '#a5b4fc', margin: '4px 0 0', fontSize: '14px' }}>
                  {selectedCustomer.name} &nbsp;•&nbsp; 📞 {selectedCustomer.phone}
                  &nbsp;•&nbsp; {customerBills.length} bill(s)
                </p>
              </div>
              <button style={S.modalClose} className="cust-btn" onClick={closeBillModal}>✕</button>
            </div>

            <div style={S.modalBody}>
              {loadingBills ? (
                <div style={S.spinnerWrap}>
                  <div style={S.spinner} />
                  <p style={{ color: '#64748b' }}>Loading bills…</p>
                </div>
              ) : customerBills.length === 0 ? (
                <div style={S.empty}>
                  <div style={S.emptyIcon}>📄</div>
                  <div style={S.emptyText}>No bills found for this customer.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={S.billTable}>
                    <thead>
                      <tr>
                        {['#', 'Bill ID', 'Bill No.', 'Date', 'Vehicle', 'Total', 'Status'].map(h => (
                          <th key={h} style={S.billTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customerBills.map((bill, i) => (
                        <tr key={i} className="cust-row" style={{ transition: 'background 0.15s' }}>
                          <td style={S.billTd}>{i + 1}</td>
                          <td style={{ ...S.billTd, color: '#818cf8', fontWeight: '600' }}>#{bill.id}</td>
                          <td style={{ ...S.billTd, fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {bill.billNumber || '—'}
                          </td>
                          <td style={{ ...S.billTd, whiteSpace: 'nowrap' }}>{formatDate(bill.createdAt)}</td>
                          <td style={S.billTd}>
                            {bill.vehicle?.number || bill.vehicleNumber || '—'}
                            {bill.vehicle?.name && ` (${bill.vehicle.name})`}
                          </td>
                          <td style={{ ...S.billTd, fontWeight: '600', color: '#6ee7b7' }}>
                            {formatCurrency(bill.summary?.total || bill.total || 0)}
                          </td>
                          <td style={S.billTd}>
                            {getPayBadge(bill.payment?.status || bill.paymentStatus)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5" style={{ ...S.billTd, textAlign: 'right', fontWeight: '700', color: '#94a3b8', fontSize: '13px' }}>
                          Total Spent:
                        </td>
                        <td style={{ ...S.billTd, fontWeight: '700', color: '#6ee7b7', fontSize: '18px' }}>
                          {formatCurrency(customerBills.reduce((sum, b) => sum + (b.summary?.total || b.total || 0), 0))}
                        </td>
                        <td style={S.billTd} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailsPage;